import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gear, Globe, Headset, Plus } from "@unified-checkout/design-system/icons/index";
import {
  Button,
  ToggleBox,
  Dropdown,
  DropdownMenu,
  DropdownButton,
  DropdownMenuItem
} from "@unified-checkout/design-system";
import type { INavbar } from "@app/dashboard/types/innerLayout/navbar";
import type { TNavbarSpecialActions } from "@app/dashboard/types/innerLayout/nav-links";
import { NAVBAR_SPECIAL_ACTIONS } from "@app/dashboard/constants/navbar-special-actions";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useNavigationStore } from "@app/dashboard/store/navigation";
import { createBtnLinks, /* supportLinks */ } from "@app/dashboard/services/inner_layout/nav-links";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { isTestLiveModeHidden } from "@app/dashboard/utils/getLiveTestSupportedRoutes";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { getJoyRideElementId, getJoyRideElementWrapperId } from "../DashboardGuide/utils";
import { useInnerLayoutLogic } from "@app/dashboard/views/layout/InnerLayout/utils";
import { PaymobPro } from "../PaymobPro";
import { BusinessInfoBox } from "../BusinessInfoBox";
import { ModeToggler } from "../ModeToggler";
import { InfoBar } from "../InfoBar";
import styles from "./navbar.module.scss";

const COLLAPSED_CREATE_BTN_WIDTH = 1000;

const Navbar = (
  { onTestToggleChange, isWidgetsLoaded }
    : INavbar
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLive } = useTestLiveModeStore();
  const { specialAction, setActiveLink } = useNavigationStore();
  const screenWidth = useScreenWidth();
  const isCreateBtnTextHidden = screenWidth < COLLAPSED_CREATE_BTN_WIDTH;

  const {
    onCreateMenuOpen,
    onCreateOptionSelected
  } = useInnerLayoutLogic();

  const handleNavbarSpecialAction = (action: TNavbarSpecialActions) => {
    specialAction?.(action);
  };

  const path = window.location.pathname;
  const isModeTogglerHidden = isTestLiveModeHidden(path);
  const isPaymobProEligible = getFromLocalStorage(LOCAL_STORAGE_KEYS.IS_PAYMOB_PRO_ENABLED);

  const create_btn_links = createBtnLinks(t);


  return (
    <>
      <nav
        className={`${styles['navbar']} ${getJoyRideElementWrapperId([1, 2, 3])}`}
      >

        <BusinessInfoBox />

        <div className={styles['navbar__actions-box']}>

          {
            !isModeTogglerHidden &&
            <div id={getJoyRideElementId(1)}>
              <ModeToggler onTestToggleChange={onTestToggleChange} />
            </div>
          }

          {/* <Dropdown width="fit-content">
            <DropdownButton
              renderAs={
                <div className={styles['navbar__actions-box__action-icon']}>
                  {Headset}
                </div>
              }
            />
            <DropdownMenu
              width="fit-content"
              horizontalPlacement="end"
            >
              {supportLinks(t).map((item, i: number) => (
                <DropdownMenuItem
                  text={item.label}
                  icon={item.icon}
                  disabled={!isWidgetsLoaded[item.action]}
                  key={`customer-support-link-${i}`}
                  onClickHandler={() => handleNavbarSpecialAction(item.action)}
                />
              ))}
            </DropdownMenu>
          </Dropdown> */}
          {isPaymobProEligible && <PaymobPro />}

          <div
            className={styles['navbar__actions-box__action-icon']}
            data-navbar-action-disabled={!isWidgetsLoaded['live-chat']}
            onClick={() => handleNavbarSpecialAction(NAVBAR_SPECIAL_ACTIONS.LIVE_CHAT)}
          >
            {Headset}
          </div>

          <div
            className={styles['navbar__actions-box__action-icon']}
            onClick={() => handleNavbarSpecialAction(NAVBAR_SPECIAL_ACTIONS.LANGUAGE)}
          >
            {Globe}
          </div>

          <div
            className={styles['navbar__actions-box__action-icon']}
            onClick={() => navigate(INNER_ROUTES.SETTINGS)}
            id={getJoyRideElementId(3)}
          >
            {Gear}
          </div>

          {
            !!create_btn_links.length &&
            <Dropdown
              id={getJoyRideElementId(2)}
              width="fit-content"
            >
              <DropdownButton
                renderAs={<Button
                  size="md"
                  color="primary"
                  variant="contained"
                  leadingIcon={Plus}
                  text={isCreateBtnTextHidden ? '' : t('innerLayout.create')}
                />}
              />
              <DropdownMenu
                width="fit-content"
                horizontalPlacement="end"
                onToggleHandler={onCreateMenuOpen}
              >
                {create_btn_links.map((item, i: number) => (
                  <DropdownMenuItem
                    key={`create-btn-link-${i}`}
                    icon={item.icon}
                    text={item.text}
                    onClickHandler={() => onCreateOptionSelected(item)}
                  />
                ))}
              </DropdownMenu>
            </Dropdown>
          }

        </div>
      </nav>

      {(!isLive && !isModeTogglerHidden) &&
        <InfoBar
          status="warning"
          text={t('innerLayout.navbar.youAreinTestMode')}
          usedFor="test_mode"
        />}
    </>
  )
}

export default Navbar;